import React, { useState, useEffect } from 'react';

/**
 * NumericInput — substitui <input type="number"> com UX melhorado.
 *
 * - Usa type="text" + inputMode="numeric" para evitar bugs do input nativo
 *   (não conseguir apagar o 0, travamentos em mobile).
 * - O campo pode ficar vazio durante a digitação; o valor só é confirmado no blur.
 * - onBlur aplica min/max e chama onChange com o número final (nunca string).
 * - Se o campo ficar vazio no blur, usa o valor mínimo (padrão 0).
 *
 * Props:
 *   value     {number}   valor controlado pelo pai
 *   onChange  {fn}       chamada com (number) no blur
 *   min       {number}   valor mínimo (default 0)
 *   max       {number}   valor máximo (default Infinity)
 *   className {string}   classes Tailwind adicionais
 *   ...rest              qualquer outro prop HTML (placeholder, disabled, etc.)
 */
const NumericInput = ({ value, onChange, min = 0, max = Infinity, className = '', ...rest }) => {
  const [internal, setInternal] = useState(String(value ?? min));

  // Sincroniza quando o pai muda o valor externamente
  useEffect(() => {
    setInternal(String(value ?? min));
  }, [value, min]);

  const handleChange = (e) => {
    // Permite apenas dígitos (e string vazia durante edição)
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setInternal(raw);
  };

  const handleBlur = () => {
    const num = parseInt(internal, 10);
    const clamped = isNaN(num) ? min : Math.min(Math.max(num, min), max);
    setInternal(String(clamped));
    if (onChange) onChange(clamped);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={internal}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      {...rest}
    />
  );
};

export default NumericInput;
